/**
 * Sheet set store (Faz 10.13, O2) — the internal sheet-set model behind the
 * Sheet Set Manager palette and the "from Sheet Set" PUBLISH source.
 *
 * Shape follows the OpenCADStudio sheet-set reality (AcSmSheetSet →
 * AcSmSubset* → AcSmSheet) reduced to what Yap needs: subsets hold sheets,
 * each sheet binds a paper-space layout (or is unlinked), carries a sheet
 * number/title/description and the "Include for Publish" flag that PUBLISH
 * honors when plotting the set into one PDF. The set round-trips inside the
 * .yap envelope (cadScene.sheetSet) — no external format dependency.
 */

import { create } from 'zustand';
import { v4 as uuidv4 } from 'uuid';

export interface SheetSetSheet {
    id: string;
    title: string;
    /** Layout the sheet plots from; null = unlinked (e.g. a future .dst import). */
    layoutId: string | null;
    /** Sheet number shown in the tree ('A-101'); not enforced unique. */
    number: string;
    description: string;
    /** "Include for Publish" — the only flag OCS persists for publishing. */
    includeForPublish: boolean;
}

export interface SheetSetSubset {
    id: string;
    name: string;
    sheets: SheetSetSheet[];
}

export interface SheetSetData {
    name: string;
    subsets: SheetSetSubset[];
}

interface SheetSetState {
    set: SheetSetData | null;
    createSet: (name: string) => void;
    deleteSet: () => void;
    renameSet: (name: string) => void;
    addSubset: (name: string) => void;
    removeSubset: (subsetId: string) => void;
    renameSubset: (subsetId: string, name: string) => void;
    moveSubset: (subsetId: string, direction: -1 | 1) => void;
    addSheet: (subsetId: string, sheet: Omit<SheetSetSheet, 'id'>) => void;
    removeSheet: (subsetId: string, sheetId: string) => void;
    updateSheet: (subsetId: string, sheetId: string, patch: Partial<Omit<SheetSetSheet, 'id'>>) => void;
    moveSheet: (subsetId: string, sheetId: string, direction: -1 | 1) => void;
    /** Envelope payload; undefined when no set exists so legacy saves keep their shape. */
    serializePayload: () => SheetSetData | undefined;
    /** Restore from cadScene.sheetSet; unknown shapes fall back to empty. */
    hydrate: (payload: unknown) => void;
}

const mapSubsets = (set: SheetSetData, fn: (subsets: SheetSetSubset[]) => SheetSetSubset[]): SheetSetData => ({
    ...set,
    subsets: fn(set.subsets),
});

const patchSubset = (set: SheetSetData, subsetId: string, fn: (subset: SheetSetSubset) => SheetSetSubset): SheetSetData =>
    mapSubsets(set, (subsets) => subsets.map((sub) => (sub.id === subsetId ? fn(sub) : sub)));

export const useSheetSetStore = create<SheetSetState>()((setState, get) => ({
    set: null,

    createSet: (name) => setState({ set: { name: name.trim() || 'My Sheet Set', subsets: [] } }),
    deleteSet: () => setState({ set: null }),
    renameSet: (name) => setState((s) => (s.set ? { set: { ...s.set, name: name.trim() || s.set.name } } : s)),

    addSubset: (name) => setState((s) => (s.set
        ? { set: mapSubsets(s.set, (subsets) => [...subsets, { id: uuidv4(), name: name.trim() || 'Subset', sheets: [] }]) }
        : s)),

    removeSubset: (subsetId) => setState((s) => (s.set
        ? { set: mapSubsets(s.set, (subsets) => subsets.filter((sub) => sub.id !== subsetId)) }
        : s)),

    renameSubset: (subsetId, name) => setState((s) => (s.set
        ? { set: patchSubset(s.set, subsetId, (sub) => ({ ...sub, name: name.trim() || sub.name })) }
        : s)),

    moveSubset: (subsetId, direction) => setState((s) => {
        if (!s.set) return s;
        return {
            set: mapSubsets(s.set, (subsets) => {
                const index = subsets.findIndex((sub) => sub.id === subsetId);
                const target = index + direction;
                if (index < 0 || target < 0 || target >= subsets.length) return subsets;
                const next = [...subsets];
                [next[index], next[target]] = [next[target], next[index]];
                return next;
            }),
        };
    }),

    addSheet: (subsetId, sheet) => setState((s) => (s.set
        ? { set: patchSubset(s.set, subsetId, (sub) => ({ ...sub, sheets: [...sub.sheets, { id: uuidv4(), ...sheet }] })) }
        : s)),

    removeSheet: (subsetId, sheetId) => setState((s) => (s.set
        ? { set: patchSubset(s.set, subsetId, (sub) => ({ ...sub, sheets: sub.sheets.filter((sh) => sh.id !== sheetId) })) }
        : s)),

    updateSheet: (subsetId, sheetId, patch) => setState((s) => (s.set
        ? {
            set: patchSubset(s.set, subsetId, (sub) => ({
                ...sub,
                sheets: sub.sheets.map((sh) => (sh.id === sheetId ? { ...sh, ...patch } : sh)),
            })),
        }
        : s)),

    moveSheet: (subsetId, sheetId, direction) => setState((s) => {
        if (!s.set) return s;
        return {
            set: patchSubset(s.set, subsetId, (sub) => {
                const index = sub.sheets.findIndex((sh) => sh.id === sheetId);
                const target = index + direction;
                if (index < 0 || target < 0 || target >= sub.sheets.length) return sub;
                const sheets = [...sub.sheets];
                [sheets[index], sheets[target]] = [sheets[target], sheets[index]];
                return { ...sub, sheets };
            }),
        };
    }),

    serializePayload: () => {
        const current = get().set;
        if (!current || (!current.name && current.subsets.length === 0)) return undefined;
        return current;
    },

    hydrate: (payload) => {
        const raw = payload as SheetSetData | null | undefined;
        if (!raw || typeof raw.name !== 'string' || !Array.isArray(raw.subsets)) {
            setState({ set: null });
            return;
        }
        // Structural parity (not strict): unknown record shapes fall back to
        // safe defaults so a hand-edited or future file never crashes load.
        const subsets: SheetSetSubset[] = [];
        for (const sub of raw.subsets) {
            if (!sub || typeof sub.id !== 'string' || typeof sub.name !== 'string' || !Array.isArray(sub.sheets)) continue;
            const sheets: SheetSetSheet[] = [];
            for (const sh of sub.sheets) {
                if (!sh || typeof sh.id !== 'string' || typeof sh.title !== 'string') continue;
                sheets.push({
                    id: sh.id,
                    title: sh.title,
                    layoutId: typeof sh.layoutId === 'string' ? sh.layoutId : null,
                    number: typeof sh.number === 'string' ? sh.number : '',
                    description: typeof sh.description === 'string' ? sh.description : '',
                    includeForPublish: sh.includeForPublish === true,
                });
            }
            subsets.push({ id: sub.id, name: sub.name, sheets });
        }
        setState({ set: { name: raw.name, subsets } });
    },
}));
